import { useCallback, useEffect, useMemo, useState } from 'react';
import { publicClient } from '../../apollo/clients';
import { PAGINATED_FAMILY_THEMES } from '../graphql/v2/queries/paginatedFamilyThemesQuery';
import { PaginateFamilyThemesQuery, PaginateFamilyThemesQueryVariables, Wws_ThemeOrder } from '../../generated/graphql';

type PaginatedFamilyThemeData = NonNullable<
  NonNullable<PaginateFamilyThemesQuery['paginateFamilyThemes']['results']>[number]
>;

export interface PaginatedFamilyTheme extends PaginatedFamilyThemeData {}

type PaginatedResponseData = PaginateFamilyThemesQuery;
interface PaginatedResponse extends PaginatedResponseData {}

type ThemeFilters = { colors?: string[] | null; styles?: string[] | null; seasons?: string[] | null } | undefined;

const keyForList = (value?: string[] | null) =>
  Array.isArray(value) && value.length ? [...value].sort().join(',') : '';

const toError = (err: unknown) => {
  const messageFromApollo = (err as any)?.graphQLErrors?.[0]?.message;
  if (typeof messageFromApollo === 'string' && messageFromApollo.length) {
    return new Error(messageFromApollo);
  }

  if (err instanceof Error) return err;
  return new Error('Unknown error');
};

export default function usePaginatedFamilyThemes(pageSize = 10, filters?: ThemeFilters) {
  const [themes, setThemes] = useState<PaginatedFamilyTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<number | null>(null);

  const filtersKey = `${keyForList(filters?.colors)}|${keyForList(filters?.seasons)}|${keyForList(filters?.styles)}`;

  const filterVariables = useMemo(() => {
    const [colorsKey, seasonsKey, stylesKey] = filtersKey.split('|');
    const colors = colorsKey ? colorsKey.split(',') : undefined;
    const seasons = seasonsKey ? seasonsKey.split(',') : undefined;
    const styles = stylesKey ? stylesKey.split(',') : undefined;

    return {
      colors,
      seasons,
      styles,
    };
  }, [filtersKey]);

  useEffect(() => {
    const fetchInitialThemes = async () => {
      try {
        setError(null);
        setLoading(true);
        setThemes([]);
        setCursor(null);
        setHasMore(false);
        const { data } = await publicClient.query<PaginatedResponse, PaginateFamilyThemesQueryVariables>({
          query: PAGINATED_FAMILY_THEMES,
          variables: { take: 6, nextCursor: null, order: Wws_ThemeOrder.RankedAsc, ...filterVariables },
        });

        setThemes(data.paginateFamilyThemes.results ?? []);
        // Cursors are positions in a specific list: a ranked cursor is meaningless in the random list,
        // so the random walk starts from the top. Filtered queries ignore order, so their cursor stays valid.
        const hasFilters = Boolean(filterVariables.colors || filterVariables.seasons || filterVariables.styles);
        setCursor(hasFilters ? data.paginateFamilyThemes.nextCursor ?? null : null);
        setHasMore(hasFilters ? data.paginateFamilyThemes.hasMore ?? false : true);
        setLoading(false);
      } catch (err) {
        setError(toError(err));
        setLoading(false);
      }
    };

    fetchInitialThemes();
  }, [filterVariables]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;

    try {
      setError(null);
      setLoading(true);
      const { data } = await publicClient.query<PaginatedResponse, PaginateFamilyThemesQueryVariables>({
        query: PAGINATED_FAMILY_THEMES,
        variables: { take: pageSize, nextCursor: cursor, order: Wws_ThemeOrder.Random, ...filterVariables },
        fetchPolicy: 'network-only',
      });

      // The random list also contains the ranked families shown on the first page
      setThemes(prev => {
        const seen = new Set(prev.map(t => t.family));
        return [...prev, ...(data.paginateFamilyThemes.results ?? []).filter(t => !seen.has(t.family))];
      });
      setCursor(data.paginateFamilyThemes.nextCursor);
      setHasMore(data.paginateFamilyThemes.hasMore);
      setLoading(false);
    } catch (err) {
      setError(toError(err));
      setLoading(false);
    }
  }, [hasMore, loading, cursor, pageSize, filterVariables]);

  return {
    themes,
    loading,
    error,
    loadMore,
    hasMore,
  };
}
